/**
 * App entry point — wires the data, map, markers and UI together.
 *
 * Keeps orchestration thin: pure logic lives in `lib/`, MapLibre glue in
 * `map/`, DOM widgets in `ui/`. This module just connects them and owns the
 * current day selection.
 */

import 'maplibre-gl/dist/maplibre-gl.css';
import './styles/main.css';

import rawItinerary from './data/itinerary.json';
import { validateItinerary } from './lib/schema';
import { ALL_DAYS, type DaySelection } from './lib/types';
import { autoSelectDay } from './lib/today';

import { createMap } from './map/mapSetup';
import { MarkerLayer } from './map/markers';
import { renderDayList, renderLegend } from './ui/dayList';
import { initOfflineIndicator } from './ui/offlineIndicator';
import { initInstallPrompt } from './ui/installPrompt';
import { initSheet } from './ui/sheet';
import { initPWA } from './pwa';

function need<T extends Element>(selector: string): T {
  const el = document.querySelector<T>(selector);
  if (!el) throw new Error(`Missing required element: ${selector}`);
  return el;
}

function main(): void {
  // 1. Load + validate data (fails loudly if the JSON is malformed).
  const itinerary = validateItinerary(rawItinerary);

  // 2. Header + legend.
  need<HTMLHeadingElement>('#trip-title').textContent = itinerary.trip.title;
  renderLegend(need<HTMLElement>('#legend'));

  // 3. Offline status + install prompt + service worker.
  initOfflineIndicator(need<HTMLElement>('#offline-status'));
  initInstallPrompt(
    need<HTMLElement>('#install-banner'),
    need<HTMLButtonElement>('#install-btn'),
    need<HTMLButtonElement>('#install-dismiss'),
    need<HTMLElement>('.install-banner__text'),
  );
  initPWA();

  // 4. Map + markers.
  const map = createMap(need<HTMLElement>('#map'));
  const markers = new MarkerLayer(map, itinerary);

  // 5. Selection state.
  let selection: DaySelection = autoSelectDay(itinerary, new Date());
  const sheet = initSheet(
    need<HTMLElement>('#day-panel'),
    need<HTMLElement>('#sheet-handle'),
    need<HTMLElement>('#sheet-scrim'),
  );

  const dayList = renderDayList(need<HTMLElement>('#day-list'), itinerary, (next) => {
    apply(next, { collapseSheet: true });
  });

  function apply(next: DaySelection, opts: { collapseSheet?: boolean; animate?: boolean } = {}): void {
    selection = next;
    dayList.setActive(selection);
    markers.render(selection);
    markers.fit(selection, { animate: opts.animate ?? true });
    if (opts.collapseSheet) sheet.collapse();
  }

  // 6. First paint. We don't wait for the map's 'load' event — basemap
  // tile-loading can delay it indefinitely on a slow/unreachable network. HTML
  // markers + fitBounds only need the container's real dimensions, so we defer
  // one animation frame (past layout), force a resize, then render + fit.
  requestAnimationFrame(() => {
    map.resize();
    apply(selection, { animate: false });
  });

  // Re-fit if the container size changes materially (sheet toggle, rotation,
  // or MapLibre's own ResizeObserver catching the first real layout).
  map.on('resize', () => markers.fit(selection, { animate: false }));

  // Expose a tiny debug handle (handy in the field; harmless otherwise).
  (window as unknown as { __trip?: unknown }).__trip = {
    selectAll: () => apply(ALL_DAYS, { collapseSheet: true }),
  };
}

main();
